import type { PlannerExercise } from '@/types'

// Rules behind the workout planner widget. Pure functions only (runs in the
// browser). Every number here comes from a source cited on the tool page:
//
// - WHO 2020 guidelines on physical activity (Bull et al., Br J Sports Med):
//   150 to 300 min of moderate aerobic activity a week, or 75 to 150 vigorous;
//   muscle strengthening on 2 or more days; adults 65+ add balance and strength
//   work on 3 or more days; start small and build up.
// - CDC, "What counts as physical activity for adults": 8 to 12 repetitions per
//   set; at least one set, two or three for more benefit; at least 2 days.
// - ACSM 2009 position stand on resistance training progression: novices 2 to
//   3 days a week, intermediate 3 to 4, advanced 4 to 5; novice loads of 8 to
//   12 RM, experienced lifters 1 to 12 RM with emphasis on heavy loads; rest
//   1 to 2 min (hypertrophy), 3 to 5 min (heavy strength), under 90 s
//   (muscular endurance); add 2 to 10 percent load once the target reps are
//   beaten by 1 or 2.
// - 2024 Adult Compendium of Physical Activities (Herrmann et al.): MET values
//   used for the calorie estimate (codes 02054, 02052, 17190, 17200). The paper
//   notes METs can be scaled to a person's own resting rate, estimated from
//   age, sex, height and weight, rather than the standard 1 kcal/kg/h.
// - Mifflin et al. 1990 (Am J Clin Nutr): resting energy expenditure, kcal/day
//   = 10 x kg + 6.25 x cm - 5 x age + 5 (men) or - 161 (women).

export type Experience = 'new' | 'some' | 'regular'
export type Goal = 'health' | 'strength' | 'fat-loss' | 'endurance'
export type Setup = 'none' | 'home' | 'gym'
export type SoreSpot = 'knees' | 'back' | 'shoulders'
export type Sex = 'female' | 'male' | 'unspecified'

export interface PlannerInput {
    age: number
    weightKg: number
    heightCm: number
    sex: Sex
    experience: Experience
    goal: Goal
    days: number
    minutes: number
    setup: Setup
    sore: SoreSpot[]
    // Answered yes to a health check question: the plan is kept gentle.
    cautious: boolean
}

export interface PlannedExercise {
    id: string
    slug: string
    name: string
    muscle: string
}

export interface Session {
    kind: 'strength' | 'cardio'
    title: string
    minutes: number
    exercises: PlannedExercise[]
    balance: boolean
}

export interface PlanDay {
    day: string
    session: Session | null
}

export interface Plan {
    days: PlanDay[]
    strengthDays: number
    sets: string
    reps: string
    rest: string
    cardioTarget: number
    cardioPlanned: number
    // Minutes of brisk walking (or similar) still needed to reach the target.
    cardioShortfall: number
    calories: [number, number]
    weeks: { title: string; text: string }[]
    notes: string[]
}

// WHO 2020: 150 to 300 min a week of moderate aerobic activity. The planner
// aims for the lower end, or the upper end when the goal is fat loss or stamina.
const CARDIO_TARGET = { low: 150, high: 300 }

// ACSM 2009 frequencies: novice 2 to 3 days, intermediate 3 to 4. The planner
// caps everyone at 4 so each muscle group is trained twice a week with a day
// between sessions for the same muscles.
const MAX_STRENGTH_DAYS: Record<Experience, number> = { new: 3, some: 4, regular: 4 }

// 2024 Compendium MET ranges: strength training 3.5 (02054, multiple exercises,
// 8 to 15 reps) to 5.0 (02052, squats and deadlifts); walking 3.8 (17190,
// moderate pace) to 4.8 (17200, brisk). Calories = MET x resting kcal per
// hour x hours, with the resting rate from Mifflin-St Jeor.
const STRENGTH_MET: [number, number] = [3.5, 5.0]
const WALK_MET: [number, number] = [3.8, 4.8]

// Mifflin et al. 1990 sex terms. Without an answer, the midpoint is used.
const MIFFLIN_SEX_TERM: Record<Sex, number> = { male: 5, female: -161, unspecified: -78 }

export const restingKcalPerDay = (weightKg: number, heightCm: number, age: number, sex: Sex): number =>
    10 * weightKg + 6.25 * heightCm - 5 * age + MIFFLIN_SEX_TERM[sex]

const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const EXERCISES_PER_SESSION: Record<number, number> = { 20: 4, 30: 5, 45: 6, 60: 7 }

const ALLOWED_EQUIPMENT: Record<Setup, string[]> = {
    none: ['bodyweight'],
    home: ['bodyweight', 'dumbbell', 'band'],
    gym: ['bodyweight', 'dumbbell', 'band', 'barbell', 'machine', 'kettlebell', 'bar']
}

// Which equipment to favour when several exercises fit.
const PREFERRED_EQUIPMENT: Record<Setup, string[]> = {
    none: ['bodyweight'],
    home: ['dumbbell', 'band', 'bodyweight'],
    gym: ['barbell', 'machine', 'dumbbell', 'bar', 'kettlebell', 'band', 'bodyweight']
}

const LEVEL_ORDER = ['beginner', 'intermediate', 'advanced']

const ALLOWED_LEVELS: Record<Experience, string[]> = {
    new: ['beginner'],
    some: ['beginner', 'intermediate'],
    regular: ['beginner', 'intermediate', 'advanced']
}

// Exercises left out when an area is sore: the ones that load it most. This is
// a cautious default, not a diagnosis, and the plan says so.
const AVOID: Record<SoreSpot, string[]> = {
    knees: ['bulgarian-split-squat', 'reverse-lunge', 'step-up', 'barbell-back-squat', 'sumo-squat', 'copenhagen-plank'],
    back: ['barbell-back-squat', 'dumbbell-romanian-deadlift', 'back-extension', 'hanging-knee-raise'],
    shoulders: ['pike-push-up', 'dumbbell-shoulder-press', 'bench-dip', 'overhead-triceps-extension', 'pull-up', 'barbell-bench-press', 'hanging-knee-raise']
}

const SORE_NOTES: Record<SoreSpot, string> = {
    knees: 'Knees: lunges, split squats and step-ups are left out. Squat only as deep as feels comfortable, and stop any exercise that causes knee pain.',
    back: 'Lower back: heavy squats, deadlifts and back extensions are left out. Keep your trunk braced and stop any exercise that causes back pain.',
    shoulders: 'Shoulders: overhead presses, dips and pull-ups are left out. Keep pressing movements in a pain-free range.'
}

// Muscle slots per session type, most important first. Sessions take as many
// as the session length allows.
const TEMPLATES: Record<string, { title: string; slots: string[] }> = {
    fullA: { title: 'Full body A', slots: ['quads', 'chest', 'upper-back', 'hamstrings', 'abs', 'shoulders', 'biceps', 'calves'] },
    fullB: { title: 'Full body B', slots: ['glutes', 'upper-back', 'chest', 'quads', 'obliques', 'triceps', 'shoulders', 'lower-back'] },
    upper: { title: 'Upper body', slots: ['chest', 'upper-back', 'shoulders', 'triceps', 'biceps', 'abs', 'traps', 'forearms'] },
    lower: { title: 'Lower body', slots: ['quads', 'hamstrings', 'glutes', 'calves', 'obliques', 'adductors', 'lower-back', 'abs'] }
}

// Training days through the week for each combination of total days and
// strength days. S = strength, C = cardio. Same-muscle sessions never fall on
// back-to-back days.
const LAYOUTS: Record<string, (string | null)[]> = {
    '2-2': ['S', null, null, 'S', null, null, null],
    '3-3': ['S', null, 'S', null, 'S', null, null],
    '4-3': ['S', null, 'S', null, 'S', 'C', null],
    '4-4': ['U', 'L', null, 'U', 'L', null, null],
    '5-3': ['S', 'C', 'S', 'C', 'S', null, null],
    '5-4': ['U', 'L', 'C', 'U', 'L', null, null]
}

const round = (value: number, step: number): number => Math.round(value / step) * step

const chooseExercise = (
    pool: PlannerExercise[],
    slot: string,
    input: PlannerInput,
    variant: number,
    used: Set<string>
): PlannerExercise | null => {
    const experience = input.cautious ? 'new' : input.experience
    const levels = ALLOWED_LEVELS[experience]
    const equipment = ALLOWED_EQUIPMENT[input.setup]
    const avoid = new Set(input.sore.flatMap((spot) => AVOID[spot]))
    const preference = PREFERRED_EQUIPMENT[input.setup]
    const topLevel = LEVEL_ORDER.indexOf(levels[levels.length - 1])

    const fits = (item: PlannerExercise) =>
        levels.includes(item.level) && equipment.includes(item.equipment) && !avoid.has(item.slug) && !used.has(item.slug)

    const rank = (item: PlannerExercise) =>
        preference.indexOf(item.equipment) * 10 + (topLevel - LEVEL_ORDER.indexOf(item.level))

    // Exercises that mainly train the slot's muscle, then ones that also work it.
    let candidates = pool.filter((item) => item.muscle === slot && fits(item))
    if (candidates.length === 0) {
        candidates = pool.filter((item) => item.alsoWorks.includes(slot) && fits(item))
    }
    if (candidates.length === 0) return null

    candidates.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
    return candidates[variant % candidates.length]
}

const buildSession = (
    pool: PlannerExercise[],
    templateKey: string,
    input: PlannerInput,
    minutes: number,
    variant: number
): Session => {
    const template = TEMPLATES[templateKey]
    const target = EXERCISES_PER_SESSION[minutes] ?? 5
    const used = new Set<string>()
    const exercises: PlannedExercise[] = []

    for (const slot of template.slots) {
        if (exercises.length >= target) break
        const pick = chooseExercise(pool, slot, input, variant, used)
        if (!pick) continue
        used.add(pick.slug)
        exercises.push({ id: pick.id, slug: pick.slug, name: pick.name, muscle: pick.muscle })
    }

    return { kind: 'strength', title: template.title, minutes, exercises, balance: false }
}

export const buildPlan = (pool: PlannerExercise[], input: PlannerInput): Plan => {
    const experience = input.cautious ? 'new' : input.experience
    const days = input.cautious ? Math.min(input.days, 3) : input.days
    const minutes = input.cautious ? Math.min(input.minutes, 30) : input.minutes

    const strengthDays = Math.max(2, Math.min(days, MAX_STRENGTH_DAYS[experience]))
    const layout = LAYOUTS[`${days}-${strengthDays}`] ?? LAYOUTS['3-3']

    let fullCount = 0
    let upperCount = 0
    let lowerCount = 0

    const planDays: PlanDay[] = layout.map((code, index) => {
        if (!code) return { day: WEEK[index], session: null }
        if (code === 'C') {
            return {
                day: WEEK[index],
                session: { kind: 'cardio', title: 'Cardio', minutes, exercises: [], balance: false }
            }
        }
        if (code === 'U') {
            const session = buildSession(pool, 'upper', input, minutes, upperCount)
            upperCount += 1
            return { day: WEEK[index], session }
        }
        if (code === 'L') {
            const session = buildSession(pool, 'lower', input, minutes, lowerCount)
            lowerCount += 1
            return { day: WEEK[index], session }
        }
        const session = buildSession(pool, fullCount % 2 === 0 ? 'fullA' : 'fullB', input, minutes, Math.floor(fullCount / 2))
        fullCount += 1
        return { day: WEEK[index], session }
    })

    // WHO: adults 65 and over add balance and strength work on 3 or more days.
    if (input.age >= 65) {
        let added = 0
        for (const entry of planDays) {
            if (entry.session && added < 3) {
                entry.session.balance = true
                added += 1
            }
        }
    }

    const highTarget = input.goal === 'fat-loss' || input.goal === 'endurance'
    const cardioTarget = highTarget ? CARDIO_TARGET.high : CARDIO_TARGET.low
    const cardioPlanned = planDays.reduce(
        (sum, entry) => sum + (entry.session?.kind === 'cardio' ? entry.session.minutes : 0),
        0
    )
    const cardioShortfall = Math.max(0, cardioTarget - cardioPlanned)

    const strengthHours = (strengthDays * minutes) / 60
    const walkHours = (cardioPlanned + cardioShortfall) / 60
    const restingPerHour = restingKcalPerDay(input.weightKg, input.heightCm, input.age, input.sex) / 24
    const calories: [number, number] = [
        round(restingPerHour * (STRENGTH_MET[0] * strengthHours + WALK_MET[0] * walkHours), 50),
        round(restingPerHour * (STRENGTH_MET[1] * strengthHours + WALK_MET[1] * walkHours), 50)
    ]

    // CDC: 8 to 12 reps; one set works, two or three bring more benefit.
    // ACSM 2009: rest 1 to 2 min, 3 to 5 min on heavy strength sets, under 90 s
    // for muscular endurance; experienced lifters use 1 to 12 RM, emphasising
    // heavier loads.
    const heavy = input.goal === 'strength' && experience !== 'new'
    const sets = experience === 'new' ? '2' : '3'
    const reps = heavy ? '6 to 12' : '8 to 12'
    const rest = input.goal === 'endurance'
        ? 'under 90 seconds'
        : heavy
            ? '1 to 2 minutes, up to 3 to 5 on your heaviest sets'
            : '1 to 2 minutes'

    const weeks = [
        {
            title: 'Week 1',
            text: 'Learn the moves. Choose a weight or version you could do a few more reps with, and focus on form.'
        },
        {
            title: 'Week 2',
            text: 'Same plan. Aim to reach the top of your rep range on every set.'
        },
        {
            title: 'Week 3',
            text: experience === 'new'
                ? 'Add a third set to each exercise. Once you can do 1 or 2 reps more than the top of your range, add 2 to 10 percent to the weight or move to the harder version.'
                : 'Once you can do 1 or 2 reps more than the top of your range, add 2 to 10 percent to the weight or move to the harder version.'
        },
        {
            title: 'Week 4',
            text: 'Repeat week 3. Then run the planner again, or keep progressing the same way.'
        }
    ]

    const notes = input.sore.map((spot) => SORE_NOTES[spot])
    if (input.cautious) {
        notes.unshift(
            'You answered yes to a health question, so this is a gentle starter plan with short sessions. Please check it with your doctor before you begin.'
        )
    }
    if (input.age >= 65) {
        notes.push(
            'Balance practice is added to 3 days, as WHO recommends for adults 65 and over. Stand next to a counter or sturdy chair you can hold.'
        )
    }

    return {
        days: planDays,
        strengthDays,
        sets,
        reps,
        rest,
        cardioTarget,
        cardioPlanned,
        cardioShortfall,
        calories,
        weeks,
        notes
    }
}
